import React from 'react';

const ANALYSIS_CONTENT_MAX = 'max-w-[1100px] w-full';

const SectionHeading = ({ children }) => (
  <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9CA3AF] mb-3">
    {children}
  </h3>
);

const confidenceClass = (level) => {
  if (level === 'High') return 'text-[#166534]';
  if (level === 'Medium') return 'text-[#B45309]';
  if (level === 'Low') return 'text-[#DC2626]';
  return 'text-[#374151]';
};

const readinessClass = (status) => {
  if (status === 'HIGH') return 'bg-[#DEF4EC] text-[#166534] border-[#C6E9D9]';
  if (status === 'MEDIUM') return 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]';
  return 'bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]';
};

const splitBoqSourceLines = (source) => {
  if (!source || source === '—') return [source ?? '—'];
  const parts = String(source)
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length ? parts : [source];
};

const BoqSourceCell = ({ source }) => {
  const lines = splitBoqSourceLines(source);
  if (lines.length <= 1) {
    return <span>{lines[0]}</span>;
  }
  return (
    <div className="space-y-1 leading-snug">
      {lines.map((line, index) => (
        <p key={index}>{line}</p>
      ))}
    </div>
  );
};

const WorkTypeBoqSection = ({ section }) => (
  <section className="rounded-xl border border-[#E5E7EB] bg-[#F0F2F4] overflow-hidden">
    <div className="flex items-center gap-2.5 bg-white px-4 py-3 border-b border-[#E5E7EB]">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#D1D5DB] bg-white text-[13px] font-semibold text-[#374151]">
        {section.index}
      </span>
      <h3 className="text-[15px] font-semibold text-[#111827]">{section.title}</h3>
      <span className="ml-auto text-[12px] font-medium text-[#6B7280]">
        {section.items.length} {section.items.length === 1 ? 'item' : 'items'}
      </span>
    </div>
    <div className="p-4">
      <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-[13px]">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                {['Item', 'Qty', 'Unit', 'Source', 'Confidence'].map((header) => (
                  <th
                    key={header}
                    className="px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-[#9CA3AF]">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.items.map((item) => (
                <tr key={item.id} className="border-b border-[#F3F4F6] last:border-b-0">
                  <td className="px-4 py-3 min-w-[200px]">
                    <p className="font-semibold text-[#111827]">{item.itemName}</p>
                    {item.description && (
                      <p className="mt-0.5 text-[11px] text-[#9CA3AF] leading-snug">{item.description}</p>
                    )}
                    {item.formula && (
                      <p className="mt-1 text-[11px] text-[#6B7280] italic">{item.formula}</p>
                    )}
                  </td>
                  <td
                    className={`px-4 py-3 font-semibold whitespace-nowrap ${
                      item.isComputable ? 'text-[#111827]' : 'text-[#DC2626]'
                    }`}>
                    {item.quantity}
                  </td>
                  <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">{item.unit}</td>
                  <td className="px-4 py-3 text-[#4B5563] max-w-[280px] align-top">
                    <BoqSourceCell source={item.source} />
                  </td>
                  <td
                    className={`px-4 py-3 font-medium whitespace-nowrap ${confidenceClass(item.confidence)}`}>
                    {item.confidence}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
);

export const BoqGeneratedOutput = ({ generatedBoqData }) => {
  if (!generatedBoqData?.workTypeSections?.length) {
    return (
      <div className={ANALYSIS_CONTENT_MAX}>
        <p className="text-[14px] text-[#6B7280]">No BOQ items in the generated response.</p>
      </div>
    );
  }

  const { finalSummary, totalBoqItems, overallConfidence, readinessStatus } = generatedBoqData;

  return (
    <div className={`${ANALYSIS_CONTENT_MAX} space-y-5`}>
      <div className="rounded-xl border border-[#E5E7EB] bg-white px-5 py-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[15px] font-semibold text-[#111827]">BOQ generated successfully</p>
          {readinessStatus && (
            <span
              className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${readinessClass(
                readinessStatus
              )}`}>
              {readinessStatus} readiness
            </span>
          )}
        </div>
        <p className="mt-2 text-[13px] text-[#6B7280]">
          {totalBoqItems ?? finalSummary?.total_boq_items ?? 0} items across{' '}
          {generatedBoqData.workTypeSections.length} work types
          {overallConfidence != null && ` · ${overallConfidence}% confidence`}
        </p>
      </div>

      <SectionHeading>Generated BOQ by work type</SectionHeading>
      {generatedBoqData.workTypeSections.map((section) => (
        <WorkTypeBoqSection key={section.id} section={section} />
      ))}
    </div>
  );
};
